import React, { createContext, useContext, useState, useEffect } from 'react';

export interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  sources?: SearchResult[];
}

export interface SearchResult {
  id: string;
  document_id: string;
  doc_marker?: string;
  content: string;
  title: string;
  similarity: number;
  created_at?: string;
  doc_type?: string;
  source_category?: string;
  is_transcript?: boolean;
  speaker_name?: string;
  start_time?: number;
  para_id?: number;
  metadata?: {
    block_id?: string;
    ref_id?: number | string;  // V3 block reference ID for highlighting
    page?: number;  // Page number for V3 block highlighting
    source_block_refs?: (number | string)[];  // All ref_ids in cited semantic chunk
    title?: string;
    analysts?: string[];
    report_date?: string;
    is_transcript?: boolean;
    speaker_name?: string;
    start_time?: number;
    location?: {
      page_number?: number;
      bbox?: number[];
      section_heading?: string;
    };
  };
}

export interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
}

interface ConversationContextType {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  createNewConversation: () => void;
  selectConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
  renameConversation: (id: string, newTitle: string) => void;
  addMessage: (message: Message) => void;
  startNewConversationWithMessages: (userMessage: Message, aiMessage: Message) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  clearCurrentConversation: () => void;
}

const ConversationContext = createContext<ConversationContextType | undefined>(undefined);

const STORAGE_KEY = 'agent_conversations';

export const ConversationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);

  // Load conversations from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert date strings back to Date objects
        const conversationsWithDates = parsed.map((conv: any) => ({
          ...conv,
          createdAt: new Date(conv.createdAt),
          updatedAt: new Date(conv.updatedAt),
          messages: conv.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
        setConversations(conversationsWithDates);
      }
    } catch (error) {
      console.error('Failed to load conversations from localStorage:', error);
    }
  }, []);

  // Save conversations to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
    } catch (error) {
      console.error('Failed to save conversations to localStorage:', error);
    }
  }, [conversations]);

  const createNewConversation = () => {
    const newConversation: Conversation = {
      id: Date.now().toString(),
      title: 'New Conversation',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversation(newConversation);
  };

  const selectConversation = (id: string) => {
    const conversation = conversations.find(c => c.id === id);
    if (conversation) {
      setCurrentConversation(conversation);
    }
  };

  const deleteConversation = (id: string) => {
    setConversations(prev => prev.filter(c => c.id !== id));
    if (currentConversation?.id === id) {
      setCurrentConversation(null);
    }
  };

  const renameConversation = (id: string, newTitle: string) => {
    setConversations(prev =>
      prev.map(conv =>
        conv.id === id
          ? { ...conv, title: newTitle, updatedAt: new Date() }
          : conv
      )
    );
    if (currentConversation?.id === id) {
      setCurrentConversation(prev => prev ? { ...prev, title: newTitle } : null);
    }
  };

  const addMessage = (message: Message) => {
    console.log('➕ addMessage called:', message.type, message.id, 'current conv:', currentConversation?.id);
    
    if (!currentConversation) {
      // Create new conversation if none exists
      const newConversation: Conversation = {
        id: Date.now().toString(),
        title: message.type === 'user' ? message.content.slice(0, 50) : 'New Conversation',
        messages: [message],
        createdAt: new Date(),
        updatedAt: new Date()
      };
      console.log('🆕 Creating new conversation:', newConversation.id);
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversation(newConversation);
    } else {
      console.log('📝 Adding to existing conversation:', currentConversation.id);
      const updatedConversation = {
        ...currentConversation,
        messages: [...currentConversation.messages, message],
        updatedAt: new Date(),
        // Auto-generate title from first user message
        title: currentConversation.messages.length === 0 && message.type === 'user'
          ? message.content.slice(0, 50) + (message.content.length > 50 ? '...' : '')
          : currentConversation.title
      };
      
      setConversations(prev =>
        prev.map(conv =>
          conv.id === currentConversation.id ? updatedConversation : conv
        )
      );
      setCurrentConversation(updatedConversation);
      console.log('✅ Message added, new total:', updatedConversation.messages.length);
    }
  };

  const startNewConversationWithMessages = (userMessage: Message, aiMessage: Message) => {
    console.log('🚀 Starting new conversation with BOTH messages');
    const newConversation: Conversation = {
      id: Date.now().toString(),
      title: userMessage.content.slice(0, 50) + (userMessage.content.length > 50 ? '...' : ''),
      messages: [userMessage, aiMessage],
      createdAt: new Date(),
      updatedAt: new Date()
    };
    console.log('🆕 Created conversation with 2 messages:', newConversation.id);
    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversation(newConversation);
  };

  const updateMessage = (messageId: string, updates: Partial<Message>) => {
    console.log('🔄 updateMessage called:', messageId, 'updates:', {
      content: updates.content?.substring(0, 50) + '...',
      contentLength: updates.content?.length,
      sources: updates.sources?.length
    });

    // IMPORTANT: If currentConversation is null during streaming (race condition),
    // find the conversation that contains this message
    let targetConversation = currentConversation;
    
    if (!targetConversation) {
      console.warn('⚠️ currentConversation is null, searching for message in all conversations...');
      console.log('📚 Total conversations:', conversations.length);
      console.log('🔍 Searching for message ID:', messageId);
      
      for (const conv of conversations) {
        console.log('📂 Checking conversation:', conv.id, 'with', conv.messages.length, 'messages');
        console.log('   Message IDs:', conv.messages.map(m => m.id));
        
        if (conv.messages.some(m => m.id === messageId)) {
          targetConversation = conv;
          console.log('✅ Found message in conversation:', conv.id);
          break;
        }
      }
      
      if (!targetConversation) {
        console.error('❌ updateMessage: Message not found in any conversation!', messageId);
        console.error('   This is likely a React state timing issue - using functional setState');
        
        // Use functional setState to work with actual current state
        // This handles the case where React hasn't re-rendered with the new conversation yet
        setConversations(prevConversations => {
          console.log('🔄 Functional setState: checking', prevConversations.length, 'conversations');
          
          let found = false;
          const updated = prevConversations.map(conv => {
            const messageIndex = conv.messages.findIndex(m => m.id === messageId);
            if (messageIndex !== -1) {
              found = true;
              console.log('✅ FOUND message in functional setState! Conv:', conv.id);
              
              const updatedMessages = [...conv.messages];
              updatedMessages[messageIndex] = {
                ...updatedMessages[messageIndex],
                ...updates
              };
              
              const updatedConv = {
                ...conv,
                messages: updatedMessages,
                updatedAt: new Date()
              };
              
              // Update currentConversation too
              setCurrentConversation(updatedConv);
              
              return updatedConv;
            }
            return conv;
          });
          
          if (!found) {
            console.error('❌ Still not found even in functional setState!');
          }
          
          return updated;
        });
        
        return; // Exit after using functional setState
      }
    }

    const updatedMessages = targetConversation.messages.map(msg => {
      if (msg.id === messageId) {
        const updated = { ...msg, ...updates };
        console.log('✅ Updating message:', msg.id, 'new content length:', updated.content.length);
        return updated;
      }
      return msg;
    });

    const updatedConversation = {
      ...targetConversation,
      messages: updatedMessages,
      updatedAt: new Date()
    };

    console.log('💾 New conversation state:', updatedConversation.messages.length, 'messages');
    
    setConversations(prev =>
      prev.map(conv =>
        conv.id === targetConversation!.id ? updatedConversation : conv
      )
    );
    
    // Update currentConversation if this is the current one
    if (currentConversation?.id === targetConversation.id || !currentConversation) {
      setCurrentConversation(updatedConversation);
      console.log('✅ Updated currentConversation');
    }
  };

  const clearCurrentConversation = () => {
    setCurrentConversation(null);
  };

  return (
    <ConversationContext.Provider
      value={{
        conversations,
        currentConversation,
        createNewConversation,
        selectConversation,
        deleteConversation,
        renameConversation,
        addMessage,
        startNewConversationWithMessages,
        updateMessage,
        clearCurrentConversation
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};

export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
};
